import clsx from "clsx"
import { WheelEvent, forwardRef, useCallback, useEffect, useRef } from "react"

import { FileThumbnail } from "@molecules"

import styles from "./FileCarousel.module.scss"
import type { FileCarouselProps } from "./FileCarousel.types"

function FileCarousel(props: FileCarouselProps, ref: React.Ref<HTMLDivElement>) {
    const { files, currentIndex, onSelect, className, ...rest } = props

    const containerRef = useRef<HTMLDivElement>(null)
    const itemRefs = useRef<(HTMLButtonElement | null)[]>([])

    // Handle horizontal scroll with mouse wheel
    const handleWheel = useCallback((event: WheelEvent<HTMLDivElement>) => {
        if (containerRef.current) {
            event.preventDefault()
            containerRef.current.scrollLeft += event.deltaY
        }
    }, [])

    useEffect(() => {
        const selectedElement = itemRefs.current[currentIndex]
        if (selectedElement) {
            selectedElement.scrollIntoView({
                behavior: "smooth",
                block: "nearest",
                inline: "center",
            })
            const carouselHasFocus = itemRefs.current.some((el) => el === document.activeElement)
            if (carouselHasFocus) {
                selectedElement.focus()
            }
        }
    }, [currentIndex])

    return (
        <div
            ref={(node) => {
                if (typeof ref === "function") {
                    ref(node)
                } else if (ref) {
                    const mutableRef = ref as React.MutableRefObject<HTMLDivElement | null>
                    mutableRef.current = node
                }
                const mutableContainerRef = containerRef as React.MutableRefObject<HTMLDivElement | null>
                mutableContainerRef.current = node
            }}
            className={clsx(styles.base, className)}
            onWheel={handleWheel}
            role="listbox"
            aria-label="File thumbnails"
            {...rest}
        >
            <div className={styles.fileThumbnails}>
                {files.map((file, index) => {
                    const isSelected = index === currentIndex

                    const handleClick = () => {
                        onSelect(index)
                        // Focus the clicked element to sync focus with selection
                        itemRefs.current[index]?.focus()
                    }

                    return (
                        <FileThumbnail
                            key={`${file.upload_filename}-${index}`}
                            ref={(el) => {
                                itemRefs.current[index] = el
                            }}
                            mimeType={file.upload_mimetype}
                            filename={file.upload_filename}
                            thumbnailSrc={file.thumbnail_url}
                            isInteractive={true}
                            isSelected={isSelected}
                            onClick={handleClick}
                            className={styles.thumbnail}
                            role="option"
                            aria-selected={isSelected}
                            aria-label={`${file.upload_filename}${isSelected ? " (selected)" : ""}`}
                            tabIndex={isSelected ? 0 : -1}
                        />
                    )
                })}
            </div>
        </div>
    )
}

export default forwardRef(FileCarousel)
